import { composeWithDevTools } from '@redux-devtools/extension'
import type { Store as __Store, Middleware, Observable } from 'redux'
import { applyMiddleware, combineReducers, compose, createStore } from 'redux'

import { AbortError } from '@app/errors/AbortError'

import type { Context } from '@app/packages/actionContext/Context'
import { spawn } from '@app/packages/disposable/spawn'
import type { ReduxSliceContainer, Serialized } from '@app/packages/reduxSlice/reduxSlice'
import { setMainReducer } from '@app/packages/thunkTransaction/thunkTransaction'

import type { StoreDispatch } from './dispatch'
import * as reducers from './reducers'
import { BASE_REDUX_SLICE_CONTAINER } from './redux_slice'
import { StoreMiddlewares } from './reduxMiddleware'

const RootReducer = combineReducers(reducers)

/** Application's Store */
export type Store = Omit<__Store<unknown>, 'dispatch'> & {
  dispatch: StoreDispatch
  [Symbol.observable](): Observable<unknown>
  sliceContainers: ReduxSliceContainer[]
  serialize: () => Serialized<unknown>
}

export type ReducerWrapper = (reducer: typeof RootReducer) => typeof RootReducer

export default function createAppStore(
  context: Context,
  sliceContainers: ReduxSliceContainer[],
  initialState?: unknown,
  reducerWrapper?: ReducerWrapper,
  middlewares?: Middleware[]
): Store {
  const shouldUseDevTools = BUILD_MODE === 'dev' && typeof window === 'object' && (window as any).__REDUX_DEVTOOLS_EXTENSION__
  const composer = shouldUseDevTools ? composeWithDevTools({ maxAge: 100, latency: 1000, shouldHotReload: false }) : compose
  const enhancer = composer(applyMiddleware(...(StoreMiddlewares(context) as any), ...(middlewares || [])))

  const slices = sliceContainers.reduce(
    (acc, cnt) => {
      acc[cnt.reducerKey] = cnt.reducer
      return acc
    },
    {} as Record<string, (state: any, action: any) => any>
  )
  let RootReducer = combineReducers({ ...reducers, ...slices })
  if (reducerWrapper) RootReducer = reducerWrapper(RootReducer)
  const deserializedState = initialState ? sliceContainers.reduce((state, container) => container.deserialize(state), initialState) : initialState
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const store = createStore(RootReducer, deserializedState as {}, enhancer) as Store
  store.dispatch(setMainReducer(RootReducer))
  store.sliceContainers = [BASE_REDUX_SLICE_CONTAINER]
  store.serialize = () => {
    let state = store.getState() as {}
    for (const cnt of sliceContainers) {
      state = cnt.serialize(state)
    }
    return state as Serialized<unknown>
  }
  return store
}

export type Selector<R> = (state: unknown) => R

/** Watches for values over store's state */
export const storeMonitoring = <T>(store: Store, selector: Selector<T>, callback: (value: T, prevValue: T, abortSignal: AbortSignal) => void): Disposable => {
  return spawn(stack => {
    const state = store.getState()
    let value = selector(state)
    let ab = stack.adopt(new AbortController(), ab => ab.abort())

    callback(value, value, ab.signal)

    stack.adopt(
      store.subscribe(() => {
        const state = store.getState()
        const nextValue = selector(state)
        const prevValue = value
        const changed = prevValue !== nextValue

        if (!changed) return

        value = nextValue

        const lab = new AbortController()
        ab.abort()
        ab = lab

        try {
          callback(nextValue, prevValue, lab.signal)
        } catch {}
      }),
      unsub => unsub()
    )
  })
}

/**
 * Wait for selector to return truthy value and returns result
 * @throws `AbortError` on abort
 */
export async function waitForValue<T>(store: Store, selector: (state: unknown) => { val: T } | null, abortSignal?: AbortSignal): Promise<T> {
  using stack = new DisposableStack()
  const state = store.getState()
  const val = selector(state)
  if (val) {
    return val.val
  }

  const p = Promise.withResolvers<T>()
  if (abortSignal) {
    const abortHandler = () => {
      p.reject(new AbortError())
    }
    abortSignal.addEventListener('abort', abortHandler)
    stack.defer(() => {
      abortSignal?.removeEventListener('abort', abortHandler)
    })
  }

  stack.use(
    storeMonitoring(store, selector, value => {
      if (value) {
        p.resolve(value?.val)
      }
    })
  )

  return await p.promise
}

/** Watches for values over store's state */
export const waitForMappedValue = async <T, B>(
  store: Store,
  selector: Selector<T>,
  mapper: (value: T) => { val: B } | null,
  abortSignal?: AbortSignal
): Promise<B> => {
  using stack = new DisposableStack()

  const p = Promise.withResolvers<B>()

  if (abortSignal) {
    const onAbort = () => {
      try {
        abortSignal.throwIfAborted()
      } catch (e) {
        p.reject(e)
      }
    }

    abortSignal?.addEventListener('abort', onAbort, { once: true })
    stack.defer(() => {
      abortSignal?.removeEventListener('abort', onAbort)
    })
  }

  stack.use(
    storeMonitoring(store, selector, val => {
      try {
        const result = mapper(val)
        if (result) {
          p.resolve(result.val)
        }
      } catch (e) {
        p.reject(e)
      }
    })
  )

  return await p.promise
}
